import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";

/** Per-user theme: 'dark' (default), 'light', 'hi-contrast'.
 *  Applies a `data-theme` attribute to <html> so CSS variables in
 *  index.css can swap the palette. Persists to user.theme_pref via PATCH.
 */
const ThemeCtx = createContext({ theme: "dark", setTheme: () => {} });

export function ThemeProvider({ children }) {
    const { user } = useAuth();
    const [theme, setThemeState] = useState(() => {
        try { return localStorage.getItem("hc-theme") || "dark"; } catch { return "dark"; }
    });

    // Apply on mount + change
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        try { localStorage.setItem("hc-theme", theme); } catch { /* noop */ }
    }, [theme]);

    // Sync from authenticated user on login
    useEffect(() => {
        if (user?.theme_pref && user.theme_pref !== theme) {
            setThemeState(user.theme_pref);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.user_id]);

    const setTheme = async (next) => {
        setThemeState(next);
        if (user) {
            try { await api.patch("/me/theme", { theme: next }); } catch { /* silent — local pref still applied */ }
        }
    };

    return <ThemeCtx.Provider value={{ theme, setTheme }}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }
